import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import apiClient from '../api/client'

interface TopicDoc {
  doc_id: number
  doi: string | null
  openalex_id: string | null
  preview: string
  page_content?: string
  cluster_id: number
  assigned_topic_prob: number | null
  x?: number
  y?: number
  topic_id?: number
  topic_name?: string
}

interface IterationData {
  iteration_index: number
  clustering_analysis: {
    topics?: {
      topic_id: number
      topic_name: string
      documents?: TopicDoc[]
    }[]
    embeddings_2d?: number[][]
  } | null
}

function DocumentsPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const [iterations, setIterations] = useState<IterationData[]>([])
  const [selectedIteration, setSelectedIteration] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDoc, setSelectedDoc] = useState<TopicDoc | null>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!id) return
    apiClient
      .get<IterationData[]>(`/research/runs/${id}/iterations`)
      .then((res) => {
        const data = res.data as unknown as IterationData[]
        setIterations(data)
        if (data.length > 0) setSelectedIteration(data[data.length - 1].iteration_index)
      })
      .catch(() => setError('Failed to load documents'))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <div className="page-header"><h1 className="page-title">Loading documents...</h1></div>
  if (error) return <div><p style={{ color: '#c00' }}>{error}</p><Link to={`/runs/${id}`}>Back to Run</Link></div>

  const currentIter = iterations.find((it) => it.iteration_index === selectedIteration)
  const allDocs: TopicDoc[] = []
  currentIter?.clustering_analysis?.topics?.forEach((t) => {
    t.documents?.forEach((d) => {
      allDocs.push({ ...d, topic_id: t.topic_id, topic_name: t.topic_name })
    })
  })

  const filtered = allDocs.filter((d) => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (
      d.preview.toLowerCase().includes(q) ||
      (d.page_content && d.page_content.toLowerCase().includes(q)) ||
      (d.doi && d.doi.toLowerCase().includes(q)) ||
      String(d.doc_id).includes(q) ||
      (d.topic_name && d.topic_name.toLowerCase().includes(q))
    )
  })

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Documents — Run {id?.slice(0, 8)}</h1>
          <p style={{ fontSize: 13, color: '#64748b' }}>{allDocs.length} documents used in clustering for this iteration</p>
        </div>
        <Link to={`/runs/${id}`} className="page-action-secondary">Back to Run</Link>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {iterations.map((it) => (
          <button
            key={it.iteration_index}
            onClick={() => setSelectedIteration(it.iteration_index)}
            className={it.iteration_index === selectedIteration ? 'button-primary' : 'page-action-secondary'}
            style={{ padding: '6px 12px', fontSize: 13 }}
          >
            Iteration {it.iteration_index} ({it.clustering_analysis?.topics?.reduce((a, t) => a + (t.documents?.length ?? 0), 0) ?? 0} docs)
          </button>
        ))}
        <Link to={`/runs/${id}/analysis`} className="page-action-secondary" style={{ marginLeft: 'auto', fontSize: 13 }}>Technical Analysis →</Link>
      </div>

      <div className="detail-card detail-card-wide" style={{ marginBottom: 16 }}>
        <input type="text" placeholder="Filter by title, DOI, ID or topic..." value={search} onChange={(e) => setSearch(e.target.value)} className="form-input" style={{ width: '100%' }} />
      </div>

      <div className="detail-card detail-card-wide">
        <h2 className="detail-card-title">Documents in iteration {selectedIteration} — {filtered.length} / {allDocs.length}</h2>
        {filtered.length === 0 ? (
          <p style={{ fontSize: 14, color: '#666' }}>{allDocs.length === 0 ? 'No documents for this iteration (empty clustering).' : 'No documents match filter.'}</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>ID</th>
                <th>Title / Preview</th>
                <th>Topic</th>
                <th style={{ width: 80, textAlign: 'right' }}>Cluster</th>
                <th style={{ width: 90, textAlign: 'right' }}>Prob</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => (
                <tr key={`${d.doc_id}-${d.topic_id}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedDoc(d)}>
                  <td>
                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.doc_id}</span>
                    {d.doi && <div style={{ fontSize: 11, color: '#2563eb', wordBreak: 'break-all' }}>{d.doi}</div>}
                    {!d.doi && d.openalex_id && <div style={{ fontSize: 11, color: '#2563eb', wordBreak: 'break-all' }}>{d.openalex_id}</div>}
                  </td>
                  <td style={{ maxWidth: 420 }}>
                    <div style={{ fontWeight: 500, color: 'var(--primary)', cursor: 'pointer' }}>{d.preview.slice(0, 120)}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, whiteSpace: 'normal', wordBreak: 'break-word' }}>{d.preview.slice(0, 180)}</div>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <span style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{d.topic_name} #{d.topic_id}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>{d.cluster_id}</td>
                  <td style={{ textAlign: 'right', fontSize: 13 }}>{d.assigned_topic_prob != null ? d.assigned_topic_prob.toFixed(2) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedDoc && (
        <div onClick={() => setSelectedDoc(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 12, maxWidth: 720, width: '100%', maxHeight: '80vh', overflow: 'auto', padding: 24, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>Document {selectedDoc.doc_id} — {selectedDoc.topic_name} #{selectedDoc.topic_id}</h2>
              <button onClick={() => setSelectedDoc(null)} style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}>Close</button>
            </div>
            <div style={{ fontSize: 13, color: '#475569', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {selectedDoc.doi && <div><strong>DOI:</strong> <a href={selectedDoc.doi.startsWith('http') ? selectedDoc.doi : `https://doi.org/${selectedDoc.doi}`} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>{selectedDoc.doi}</a></div>}
              {selectedDoc.openalex_id && <div><strong>OpenAlex:</strong> <a href={selectedDoc.openalex_id} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>{selectedDoc.openalex_id}</a></div>}
              <div><strong>Topic:</strong> {selectedDoc.topic_name} #{selectedDoc.topic_id} · Cluster {selectedDoc.cluster_id} · Prob {selectedDoc.assigned_topic_prob?.toFixed(3) ?? '-'}</div>
              <div style={{ marginTop: 8 }}><strong>Document:</strong><p style={{ whiteSpace: 'pre-wrap', marginTop: 4, background: '#f8fafc', padding: 12, borderRadius: 8, maxHeight: 400, overflow: 'auto' }}>{(selectedDoc as unknown as { page_content?: string }).page_content ?? selectedDoc.preview}</p></div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DocumentsPage
